import type { paths } from './schema.gen';
export type ApiPath = keyof paths & string;
export type PendingPath = keyof typeof PENDING_PATHS;
export type PendingEntry = { readonly [K in PendingPath]: string };
/** 待建端点没有 spec 条目 ⇒ 线上形状无从推导；`unknown` 而非 `any`（形状未知 ≠ 放弃检查）。 */
export type UnbuiltShape = unknown;
export type Method = 'get' | 'post' | 'patch' | 'put' | 'delete';
type Op<P extends ApiPath, M extends Method> = NonNullable<paths[P][M]>;
type FirstSuccess<R> = 200 extends keyof R
  ? R[200]
  : 201 extends keyof R
    ? R[201]
    : 202 extends keyof R
      ? R[202]
      : 204 extends keyof R
        ? R[204]
        : never;
type JsonBody<T> = T extends { content: { 'application/json': infer J } } ? J : void;
/**
 * pipe 的所有 2xx 响应都裹在 `{code, data, message, request_id}` 信封里
 * （backend 是裸返回）。`Res` 一律指**信封里的 data**，运行时解包在
 * `http.ts::unwrapEnvelope` —— 两处必须同时改。
 */
type DataOf<T> = T extends { data?: infer D } ? NonNullable<D> : T;
export type Res<P extends ApiPath | PendingPath, M extends Method> = P extends PendingPath
  ? UnbuiltShape
  : P extends ApiPath
    ? DataOf<JsonBody<FirstSuccess<Op<P, M>['responses']>>>
    : never;
export type Body<P extends ApiPath | PendingPath, M extends Method> = P extends PendingPath
  ? UnbuiltShape
  : P extends ApiPath
    ? Op<P, M> extends { requestBody: { content: { 'application/json': infer B } } }
      ? B
      : never
    : never;
export type PathParams<P extends ApiPath | PendingPath, M extends Method> = P extends PendingPath
  ? never
  : P extends ApiPath
    ? Op<P, M> extends { parameters: { path: infer PP } }
      ? PP
      : never
    : never;
export const PENDING_PATHS = {
  '/api/v1/audit/search': '审计域已裁定不建',
  '/api/v1/audit/stats': '审计域已裁定不建',
} as const satisfies Partial<Record<string, string>>;
export const FIELD_GAPS = {
  'MeOut.username':
    'pipe 的 /auth/me 只回 {id, role}；完整用户行由 TokenPairOut.user 携带，前端已绕开',
  'MeOut.email': '同上 —— 别把「单独拉 me 拿全行」的旧路改回来',
} as const satisfies Readonly<Record<string, string>>;
export type FieldGapKey = keyof typeof FIELD_GAPS;
export const EP = {
  ordersCreate: '/api/v1/orders/create',
  ordersAccountsUpdate: '/api/v1/orders/accounts/update',
  ordersAccountsDelete: '/api/v1/orders/accounts/delete',
  redeemSubmit: '/api/v1/redeem/submit',
  redeemHistory: '/api/v1/redeem/search',
  redeemStats: '/api/v1/redeem/stats',
  redeemExportOptions: '/api/v1/redeem/export-options',
  redeemExport: '/api/v1/redeem/export',
  usersSearch: '/api/v1/users/search',
  usersStats: '/api/v1/users/stats',
  usersLookupUser: '/api/v1/users/lookup-user',
  usersCreate: '/api/v1/users/create',
  usersUpdate: '/api/v1/users/update',
  usersUpdateMe: '/api/v1/users/update-me',
  usersLogout: '/api/v1/users/logout',
  usersDelete: '/api/v1/users/delete',
  usersVerify: '/api/v1/users/verify',
  accountsVerify: '/api/v1/accounts/verify',
  accountsKeepAlive: '/api/v1/accounts/keep-alive',
  accountsAutoKeepAlive: '/api/v1/orders/accounts/auto-keep-alive',
  accountsSteamHistory: '/api/v1/accounts/steam-history',
  accountsUpdate: '/api/v1/accounts/update',
  accountsDelete: '/api/v1/accounts/delete',
  ordersAccountsSearch: '/api/v1/orders/accounts/search',
  ordersAccountsStats: '/api/v1/orders/accounts/stats',
  ordersAccountsGrant: '/api/v1/orders/accounts/grant',
  ordersAccountsRevoke: '/api/v1/orders/accounts/revoke',
  ordersAccountsGrants: '/api/v1/orders/accounts/grants',
  cdkeysCreate: '/api/v1/cdkeys/create',
  cdkeysSearch: '/api/v1/cdkeys/search',
  cdkeysStats: '/api/v1/cdkeys/stats',
  cdkeysUpdate: '/api/v1/cdkeys/update',
  cdkeysDelete: '/api/v1/cdkeys/delete',
  cdkeysBind: '/api/v1/public/cdkeys/bind',
  cdkeysPublicStatus: '/api/v1/public/cdkeys/status',
  cdkeysPublicSubmitCode: '/api/v1/public/cdkeys/submit-code',
  fxSearch: '/api/v1/fx/search',
  proxiesSearch: '/api/v1/proxies/search',
  proxiesStats: '/api/v1/proxies/stats',
  proxiesCreate: '/api/v1/proxies/create',
  proxiesUpdate: '/api/v1/proxies/update',
  proxiesDelete: '/api/v1/proxies/delete',
  proxiesCheckSpeed: '/api/v1/proxies/check-speed',
  proxiesAutoCheckSpeed: '/api/v1/proxies/auto-check-speed',
  commonDownload: '/api/v1/common/download',
  authLogin: '/api/v1/auth/login',
  authRefresh: '/api/v1/auth/refresh',
  authLogout: '/api/v1/auth/logout',
  notificationsList: '/api/v1/notifications',
  notificationsUnreadCount: '/api/v1/notifications/unread-count',
  notificationsMarkRead: '/api/v1/notifications/{notification_id}/read',
  notificationsBroadcast: '/api/v1/notifications/broadcast',
} as const satisfies Record<string, ApiPath | PendingPath>;
export type EpKey = keyof typeof EP;
export type EpPath = (typeof EP)[EpKey];
type _pathTableIsValid = Exclude<EpPath, ApiPath | PendingPath>;
const _valid: [_pathTableIsValid] extends [never]
  ? true
  : ['❌ spec 里没有的端点', _pathTableIsValid] = true;
const NOT_CONSUMED = {
  '/api/v1/auth/me': '只回 {id, role}；完整用户行由 /auth/login 与 /auth/refresh 的 user 字段携带',
  '/api/v1/fx/{fx_id}': '汇率单条查询，报价页从 /fx/search 一页取全量快照，单条无调用点',
  '/api/v1/orders/{order_no}/accounts/{account}':
    '挂载行详情单条，前端从 /orders/accounts/search 列表读',
  '/health': '前端探活走 nginx /healthz，不经后端',
  '/api/v1/accounts/delete':
    'Admin 档账号轴删除（无页面接线 —— 页面删除走挂载级 ' +
    '/orders/accounts/delete；管理面就绪时再接线）',
  '/api/v1/accounts/relogin':
    '点名重登（后端已实现）—— 前端这一步改走 keep-alive（刷新，刷不动才重登）后暂无调用点',
  '/api/v1/orders/accounts/auto-relogin':
    '一键重登（后端已实现）—— 前端这一步改走 auto-keep-alive 后暂无调用点',
} as const satisfies Partial<Record<ApiPath, string>>;
export type NotConsumedPath = keyof typeof NOT_CONSUMED;
export const NOT_CONSUMED_REASONS: Readonly<Record<NotConsumedPath, string>> = NOT_CONSUMED;
type _everySpecPathIsAccounted = Exclude<ApiPath, EpPath | NotConsumedPath>;
const _complete: [_everySpecPathIsAccounted] extends [never]
  ? true
  : ['❌ spec 里有、端点表漏了、也没登记不消费的理由', _everySpecPathIsAccounted] = true;
export type __ContractProofs = [typeof _valid, typeof _complete];
